import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config';
import { showToast } from '../../components/toast';
import DataTable from 'react-data-table-component';
import LoadingSpinner from "../../components/LoadingSpinner";

const FolioViewGeneralNotification = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [notification, setNotification] = useState(null);
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterText, setFilterText] = useState('');

    // Fetch notification details
    const fetchNotificationDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');

            const response = await axios.get(`${API_URL}FolioPushNotification/GeneralNotification/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data.success) {
                const notificationData = response.data.data || response.data.notify || response.data.notification;

                if (notificationData) {
                    setNotification({
                        id: notificationData.id,
                        title: notificationData.title,
                        message: notificationData.description || notificationData.message,
                        created_on: notificationData.date_time || notificationData.created_on,
                        sent_count: notificationData.sent_count,
                        status: notificationData.status
                    });
                }

                setSubscribers(response.data.viewlist || response.data.subscribers || response.data.recipients || []);
            } else {
                showToast.error(response.data.message || 'Failed to fetch folio notification details');
                navigate('/Campaign/FolioGeneralNotification');
            }
        } catch (error) {
            console.error('Error fetching folio notification details:', error);
            if (error.response && error.response.status === 404) {
                showToast.error('Folio Notification not found');
            } else {
                showToast.error('Failed to fetch folio notification details');
            }
            navigate('/Campaign/FolioGeneralNotification');
        } finally {
            setLoading(false);
        }
    };

    // Handle export
    const handleExport = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');

            const exportUrl = `${API_URL}FolioPushNotification/GeneralNotification/export/${id}`;

            const response = await fetch(exportUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const blob = await response.blob();

                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = `folio-general-notification-${id}-details.xlsx`;

                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                    if (filenameMatch) {
                        filename = filenameMatch[1];
                    }
                }

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                showToast.success("Export completed successfully");
            } else {
                throw new Error(`Export failed with status: ${response.status}`);
            }
        } catch (error) {
            console.error("Export error:", error);
            showToast.error("Failed to export folio notification details");
        } finally {
            setLoading(false);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';

        if (dateString.includes('-') && dateString.includes(':')) {
            return dateString;
        }

        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return dateString;
        }
        return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString();
    };

    // Filter subscribers
    const filteredSubscribers = subscribers.filter(subscriber => {
        if (!filterText) return true;
        return (
            (subscriber.fullname && subscriber.fullname.toLowerCase().includes(filterText.toLowerCase())) ||
            (subscriber.name && subscriber.name.toLowerCase().includes(filterText.toLowerCase())) ||
            (subscriber.mobile && subscriber.mobile.includes(filterText)) ||
            (subscriber.mobile_no && subscriber.mobile_no.includes(filterText)) ||
            (subscriber.email && subscriber.email.toLowerCase().includes(filterText.toLowerCase()))
        );
    });

    // Table columns
    const columns = [
        {
            name: '#',
            selector: (row, index) => index + 1,
            width: '60px'
        },
        {
            name: 'Subscriber Name',
            selector: row => row.fullname || row.name || 'N/A',
            sortable: true
        },
        {
            name: 'Mobile No.',
            selector: row => row.mobile || row.mobile_no || 'N/A',
            sortable: true
        }
    ];

    // Fetch data on component mount
    useEffect(() => {
        if (id) {
            fetchNotificationDetails();
        } else {
            showToast.error('Invalid notification ID');
            navigate('/Campaign/FolioGeneralNotification');
        }
    }, [id, navigate]);

    if (loading && !notification) {
        return (
            <LoadingSpinner />
        );
    }

    if (!notification) {
        return (
            <div className="page-container mt-5">
                <div className="d-flex justify-content-center py-5">
                    <div className="alert alert-warning">
                        <h4>Folio Notification Not Found</h4>
                        <p>The requested folio notification could not be found.</p>
                        <Link to="/Campaign/FolioGeneralNotification" className="btn btn-primary">
                            Back to Folio General Notifications
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container mt-5">
            {/* Breadcrumb */}
            <div className="d-flex align-items-center mb-3">
                <Link to="/" className="text-primary text-decoration-none">
                    <i className="fas fa-home me-1"></i> Home
                </Link>
                <span className="mx-2">/</span>
                <Link to="/Campaign/FolioGeneralNotification" className="text-primary text-decoration-none">
                    Folio General Notification
                </Link>
                <span className="mx-2">/</span>
                <span>Details</span>
            </div>

            {/* Actions */}
            <div className="row pb-2">
                <div className="col-md-3"></div>
                <div className="col-md-9 text-end">
                    <button
                        className="btn btn-default btn-sm"
                        onClick={handleExport}
                    >
                        <i className="fa fa-upload"></i> Export
                    </button>
                </div>
            </div>

            {/* Notification Details */}
            <div className="panel panel-default with-rounded-corner with-shadow p-10">
                <div className="panel-heading pb-4" style={{ border: '1px solid #ececec' }}>
                    <p><b>Title: </b>{notification?.title}</p>
                    <p><b>Message: </b>{notification?.message}</p>
                    <p><b>Created On: </b>{formatDate(notification?.created_on)}</p>
                </div>

                {/* Subscribers Table */}
                {subscribers.length > 0 ? (
                    <div className="mt-3">
                        <div className="mb-3">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Filter by name or mobile..."
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                        </div>
                        <DataTable
                            columns={columns}
                            data={filteredSubscribers}
                            pagination
                            paginationPerPage={50}
                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                            noHeader
                            dense
                            highlightOnHover
                            responsive
                            customStyles={{
                                headCells: {
                                    style: {
                                        paddingLeft: '8px',
                                        paddingRight: '8px',
                                    },
                                },
                                cells: {
                                    style: {
                                        paddingLeft: '8px',
                                        paddingRight: '8px',
                                    },
                                },
                            }}
                        />
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <p>Sent To All Mobile Users</p>
                    </div>
                )}
            </div>

            <div id='myModal' className='modal'>
                <div className="modal-dialog panel panel-default panel-border top">
                    <div className="modal-content">
                        <div id='myModalContent'></div>
                    </div>
                </div>
            </div>

            <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
                <i className="ti-arrow-up"></i>
            </a>
        </div>
    );
};

export default FolioViewGeneralNotification;
